import { template as template_5a5286f998fa4d07a338642e6f0ba6e5 } from "@ember/template-compiler";
import { service } from '@ember/service';
import { isBlank, isEmpty, isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { sortByProp } from 'ramda-adjunct';
import FilterWrapper from 'eflex/components/filters/filter-wrapper';
import { t } from 'ember-intl';
import { notEq, and } from 'ember-truth-helpers';
import { fn, concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { isPresent as isPresentHelper } from '@eflexsystems/ember-composable-helpers';
import CheckboxInput from 'eflex/components/checkbox-input';
import InputsLiveSearch from 'eflex/components/inputs/search-live';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import BsButton from 'eflex/components/bs-button';
import Portal from 'ember-stargate/components/portal';
import SearchTag from 'eflex/components/filters/search-tag';
class TreeItem extends Component {
    @tracked
    _expanded;
    constructor(){
        super(...arguments);
        this._expanded = true;
    }
    get isChecked() {
        let children;
        const treeItem = this.args.treeItem;
        if (treeItem.type === this.args.type) {
            children = [
                treeItem
            ];
        } else if (this.args.type === 'station') {
            children = treeItem.stations;
        } else {
            children = treeItem.tasks;
        }
        const selectedTreeItems = this.args.selectedTreeItems;
        return children.some((child)=>selectedTreeItems.has(child));
    }
    get expanded() {
        return this._expanded || !isBlank(this.args.searchTerm);
    }
    get filteredChildren() {
        let sortedChildren;
        if (this.args.treeItem.type === 'station') {
            sortedChildren = this.args.treeItem.sortedChildren;
        } else {
            sortedChildren = sortByProp('order', this.args.treeItem.children ?? []);
        }
        if (isBlank(this.args.searchTerm)) {
            return sortedChildren;
        }
        const searchRegex = new RegExp(this.args.searchTerm, 'i');
        return sortedChildren.filter((treeItem)=>searchRegex.test(treeItem.name) || treeItem.children?.some((_treeItem)=>searchRegex.test(_treeItem.name)) || treeItem.tasks?.some((_treeItem)=>searchRegex.test(_treeItem.name)));
    }
    get hasChildren() {
        if (this.args.treeItem.type === this.args.type) {
            return false;
        }
        return !isEmpty(this.args.treeItem.children);
    }
    get expandedClass() {
        if (!this.expanded) {
            return null;
        }
        if (this.args.treeItem.type === 'area' || this.args.treeItem.type === 'group') {
            return 'tree-toggle-down';
        } else {
            return 'tree-station-opened';
        }
    }
    expand = ()=>{
        this._expanded = !this._expanded;
    };
    static{
        template_5a5286f998fa4d07a338642e6f0ba6e5(`
    <li
      class="plant-{{@treeItem.type}} text-nowrap fs-4"
      ...attributes
    >
      <div class="d-flex align-items-center">
        <span class="tree-menu-item">
          <label class="tree-item-checkbox">
            <CheckboxInput
              class="eflex-tree-checkbox"
              @checked={{this.isChecked}}
              @onChange={{fn @onTreeItemChecked @treeItem}}
            />
          </label>
        </span>
        <span class="tree-item-label flex-fill toggle-children tree-{{@treeItem.type}}"
          {{on "click" this.expand}}
        >
          {{#if this.hasChildren}}
            <FaIcon @icon="caret-{{if this._expanded 'down' 'right'}}" @prefix="fas" class="expand me-1" />
          {{/if}}
          {{@treeItem.name}}
        </span>
      </div>
      {{#if (and (notEq @treeItem.type 'task') this.hasChildren this._expanded)}}
        <ul class='{{@treeItem.type}}-children list-unstyled'>
          {{#each this.filteredChildren as |child|}}
            <TreeItem
              @type={{@type}}
              @selectedTreeItems={{@selectedTreeItems}}
              @treeItem={{child}}
              @searchTerm={{@searchTerm}}
              @onTreeItemChecked={{@onTreeItemChecked}}
            />
          {{/each}}
        </ul>
      {{/if}}
    </li>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export default class PlantFilter extends Component {
    @service
    intl;
    @service
    store;
    @service
    taskRepo;
    @service
    stationRepo;
    @service
    areaRepo;
    @tracked
    searchTerm;
    @tracked
    isDisplayed;
    constructor(){
        super(...arguments);
        this.isDisplayed = isPresent(this.args[this.paramName]);
        this.args.initParam(this.paramName, this.args[this.paramName]);
    }
    get tagDisplayed() {
        const tree = this.args[this.paramName];
        if (tree) {
            return tree.length > 0;
        }
        return false;
    }
    get hasNoneSelected() {
        return isEmpty(this.treeFilterSelectedItems);
    }
    get isIndeterminate() {
        return !this.isAllSelected && !this.hasNoneSelected;
    }
    get selectedTreeItemsText() {
        const selected = [
            ...this.args.selectedTreeItems
        ];
        const length = selected.length;
        if (length === 1) {
            return selected[0].name;
        }
        let caption;
        if (this.args.type === 'station') {
            caption = 'criteria.treeSearchStations';
        } else {
            caption = 'criteria.treeSearchTasks';
        }
        return this.intl.t(caption, {
            length
        });
    }
    get filteredAreas() {
        let areas = this.areaRepo.areas;
        if (!isBlank(this.searchTerm)) {
            const searchRegex = new RegExp(`${this.searchTerm}`, 'i');
            areas = areas.filter((area)=>searchRegex.test(area.name) || area.children.some((group)=>searchRegex.test(group.name)) || area.stations.some((station)=>searchRegex.test(station.name)) || area.tasks.some((treeTask)=>searchRegex.test(treeTask.name)));
        }
        return sortByProp('order', areas ?? []);
    }
    updateItemIds = (tree)=>{
        tree = [
            ...tree
        ];
        if (isEmpty(tree)) {
            this.args.updateParam(this.paramName, []);
        } else {
            const treeItemIds = tree.map((item)=>item.id);
            this.args.updateParam(this.paramName, treeItemIds);
        }
    };
    toggleAll = ()=>{
        if (this.isAllSelected) {
            this.updateItemIds([]);
            return;
        }
        this.updateItemIds(this.allPossibleItems);
    };
    onTreeItemChecked = (treeItem, isChecked)=>{
        const selectedTreeItems = new Set(this.treeFilterSelectedItems);
        let children;
        if (treeItem.type === this.args.type) {
            children = [
                treeItem
            ];
        } else if (this.args.type === 'station') {
            children = treeItem.stations;
        } else {
            children = treeItem.tasks;
        }
        children.forEach((child)=>{
            if (isChecked) {
                selectedTreeItems.add(child);
            } else {
                selectedTreeItems.delete(child);
            }
        });
        this.updateItemIds(selectedTreeItems);
    };
    get getParam() {
        return this.args.getParam(this.paramName);
    }
    get treeFilterSelectedItems() {
        const tree = this.getParam;
        const treeItems = tree?.map((treeItemId)=>this.store.peekRecord(this.args.type, treeItemId));
        return new Set(treeItems ?? []);
    }
    get isAllSelected() {
        const allItems = this.allPossibleItems;
        const selectedItems = this.treeFilterSelectedItems;
        return allItems.length === selectedItems.size;
    }
    get treeFilterSelectedItemsText() {
        const tree = this.args[this.paramName];
        if (tree?.length === this.allPossibleItems.length) {
            return this.intl.t('searchingAllStations');
        }
        const selected = tree?.map((treeItemId)=>this.store.peekRecord(this.args.type, treeItemId));
        const length = selected.length;
        let caption, label;
        if (this.args.type === 'station') {
            label = this.intl.t('station');
            caption = 'criteria.treeSearchStations';
        } else {
            label = this.intl.t('task');
            caption = 'criteria.treeSearchTasks';
        }
        if (length === 1) {
            return `${label}: ${selected[0].name}`;
        }
        return this.intl.t(caption, {
            length
        });
    }
    get paramName() {
        if (this.args.type === 'station') {
            return 'stationIds';
        } else {
            return 'taskIds';
        }
    }
    get allPossibleItems() {
        if (this.args.type === 'station') {
            return this.stationRepo.stations;
        } else {
            return this.taskRepo.tasks;
        }
    }
    onDisplayedChanged = ()=>{
        this.isDisplayed = !this.isDisplayed;
    };
    clear = ()=>{
        this.updateItemIds([]);
    };
    static{
        template_5a5286f998fa4d07a338642e6f0ba6e5(`
    <Portal @target="filter-fields">
      <FilterWrapper
        class="eflex-tree-criteria"
        @form={{@form}}
        @label={{@type}}
        @isDisplayed={{this.isDisplayed}}
        @type={{@type}}
        @onChecked={{this.onDisplayedChanged}}
        @hasValue={{isPresent this.treeFilterSelectedItems}}
        ...attributes
      >
        <div class="row g-2 mb-3">
          <div class='col'>
            <InputsLiveSearch
              class="app-search-box"
              @searchValue={{this.searchTerm}}
              @placeholder={{concat (t "controls.tree.plantsearch") '...'}}
              @onInput={{fn (mut this.searchTerm)}}
              @inputClass="test-tree-item-search"
            />
          </div>
        </div>

        <ul
          class="
            tree-list
            list-unstyled
            overflow-y-auto
            row
            flex-column
            flex-nowrap
            mb-0
            {{if (isPresentHelper this.searchTerm) "searching"}}
          "
        >
          <div class="col-auto d-flex align-items-center bg-white sticky-top mb-1">
            <CheckboxInput
              class="checkbox-all"
              @checked={{this.isAllSelected}}
              @onChange={{this.toggleAll}}
              @indeterminate={{this.isIndeterminate}}
              @label={{t "selectAll"}}
              @labelClass="fw-bold fs-4"
            />
            {{#if (isPresent this.treeFilterSelectedItems)}}
              <BsButton
                @onClick={{this.clear}}
                @type="link"
                class="link-danger p-0 fs-4 text-decoration-none fw-semibold ms-2"
              >
                {{t "clear"}}
              </BsButton>
            {{/if}}
          </div>
          <div class="col-auto">
            {{#each this.filteredAreas as |treeItem|}}
              <TreeItem
                @type={{@type}}
                @selectedTreeItems={{this.treeFilterSelectedItems}}
                @treeItem={{treeItem}}
                @searchTerm={{this.searchTerm}}
                @onTreeItemChecked={{this.onTreeItemChecked}}
              />
            {{/each}}
          </div>
        </ul>
      </FilterWrapper>
    </Portal>
    <Portal @target="search-badge-tags">
      <SearchTag
        class='treeFilter treeFilter_station'
        @isDisplayed={{this.tagDisplayed}}
        @text={{this.treeFilterSelectedItemsText}}
      />
    </Portal>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
